import { Injectable, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { map, tap, catchError, first, switchMap, finalize, filter } from "rxjs/operators";
import { EMPTY, merge, Observable } from "rxjs";
import * as Sentry from "@sentry/angular";

import { AWSHttpClient } from "@intocare-medapp/aws/http";

import { userBriefingActions, Model } from "../../shared";
import { FilterPageOptionEnum } from "../../shared/enums";
import { ApplicationState, Briefing } from "../../shared/types";
import { BriefingFilterService } from "../util/briefing-filter.service";
import { FilterModel } from "../../filter/filter.model";

/**
 * List of error status codes that shouldn't be reported to Sentry.
 */
const errorCodesToIgnore = new Set([0, 401]);

@Injectable()
export class UserBriefingModel extends Model {
	private readonly http = inject(AWSHttpClient);
	private readonly store = inject<Store<ApplicationState>>(Store);
	private readonly briefingFilterService = inject(BriefingFilterService);
	private readonly filterModel = inject(FilterModel);

	private readonly briefing$ = this.getAllBriefing();

	filter$ = this.briefingFilterService.filter$;
	loading$ = this.store.select((state) => {
		return state.data.userBriefing.ui.loading;
	});
	filteredBriefingHistory$: Observable<Briefing[]> = this.briefing$.pipe(
		switchMap((briefings) => this.briefingFilterService.getFilteredBriefings(briefings)),
	);

	private getAllBriefing(): Observable<Briefing[]> {
		return merge(
			this.store.select((state) => state.data.userBriefing.data).pipe(map(({ items }) => items || [])),
			this.store
				.select((state) => state.data.userBriefing.error)
				.pipe(
					filter((hasError) => Boolean(hasError)),
					map(() => []),
				),
		);
	}

	search(text: string) {
		this.briefingFilterService.search(text);
	}

	loadUserBriefing() {
		this.store.dispatch(userBriefingActions.loading(true));

		return this.http
			.get<{
				history: Briefing[];
				briefingRetrievalDate: string;
			}>("briefing/v2/user")
			.pipe(
				catchError((err) => {
					this.store.dispatch(userBriefingActions.error(err));

					if (errorCodesToIgnore.has(err.status)) {
						return EMPTY;
					}

					const briefingError = new Error("Failed to load user briefing", { cause: err });
					Sentry.captureException(briefingError);

					return EMPTY;
				}),
				tap((result) => this.store.dispatch(userBriefingActions.set(result))),
				finalize(() => {
					this.store.dispatch(userBriefingActions.loading(false));
				}),
			);
	}

	confirmBriefing() {
		return this.store
			.select((state) => state.data.userBriefing.data)
			.pipe(
				first(),
				switchMap(({ briefingRetrievalDate }) =>
					// @ts-expect-error TS2345
					this.performAsyncAction(userBriefingActions.confirm(briefingRetrievalDate)),
				),
			);
	}

	selectFilter(filterId: FilterPageOptionEnum) {
		this.filterModel.selectFilter(filterId);
	}
}
